/**
 * FuturesReportTable — shared table + style cho các report dạng Futures Summary.
 *
 * Trích từ `FuturesSummaryReportPage.tsx` để dùng lại ở các module khác
 * (Factory BL Summary, Margin Report, v.v.). Style giữ nguyên 1-1: cream + gold,
 * cell height 20px, font 0.65rem, total row gold gradient, sticky header/footer.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
} from '@mui/material'

const useIsoLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

export type ReportRow = Record<string, unknown>

export type ColumnDef = {
  key: string
  label: string
  /** Optional group caption — đặt ở header row đầu (above label), các cột liên
   *  tiếp cùng `group` sẽ merge bằng colSpan. Cột không có group nhưng nằm
   *  giữa các cột có group sẽ chiếm 1 cell trống ở row group. */
  group?: string
  numeric?: boolean
  width?: string
  minWidth?: number
  sourceKeys?: string[]
}

/** Tier 1 — sub-report caption: cream → gold nhạt, viền copper. */
export const sectionTitleSx = {
  height: 20,
  px: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.66rem',
  fontWeight: 900,
  letterSpacing: '0.07em',
  color: '#000000',
  background: 'linear-gradient(180deg, #ffffff 0%, #fffbf4 64%, rgba(244, 223, 156, 0.36) 100%)',
  border: '1px solid rgba(218, 131, 60, 0.16)',
  borderBottom: '2px solid rgba(169, 94, 37, 0.22)',
  borderTopLeftRadius: '6px',
  borderTopRightRadius: '6px',
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.9)',
  textTransform: 'uppercase' as const,
}

/** Tier 2 — band rộng (TOTAL POSITIONS): gold đậm hơn. */
export const darkTitleSx = {
  height: 22,
  px: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.7rem',
  fontWeight: 900,
  letterSpacing: '0.08em',
  color: '#000000',
  background: 'linear-gradient(180deg, #fff8e6 0%, #f4df9c 52%, #e4c36c 100%)',
  border: '1px solid rgba(169, 94, 37, 0.34)',
  borderBottom: '2px solid rgba(11, 54, 51, 0.24)',
  borderTopLeftRadius: '6px',
  borderTopRightRadius: '6px',
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.72)',
  textTransform: 'uppercase' as const,
}

/** Tier 1b — copper accent (PROPS): stripe trái, peach nhạt. */
export const propTitleSx = {
  ...sectionTitleSx,
  color: '#000000',
  background: 'linear-gradient(180deg, #ffffff 0%, #fff4e8 76%, rgba(251, 212, 168, 0.5) 100%)',
  border: '1px solid rgba(218, 131, 60, 0.22)',
  borderBottom: '2px solid rgba(169, 94, 37, 0.28)',
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.9), inset 3px 0 0 rgba(218, 131, 60, 0.45)',
}

const totalRowSx = {
  fontWeight: 800,
  color: '#000000',
}

const totalRowBodySx = {
  ...totalRowSx,
  backgroundColor: 'rgba(244, 223, 156, 0.16)',
  borderTop: '1px solid rgba(169, 94, 37, 0.22)',
}

const totalRowFooterSx = {
  ...totalRowSx,
  background: 'linear-gradient(180deg, #fffefb 0%, #fbf0d4 100%)',
  borderTop: '2px solid rgba(11, 54, 51, 0.2)',
}

/** Sub-total (WIP, partial) row — nhạt hơn dòng total chính, không bold đậm,
 *  giữ tone gold để gắn vào cụm footer. */
const subtotalRowFooterSx = {
  fontWeight: 700,
  color: '#000000',
  fontStyle: 'italic' as const,
  background: 'linear-gradient(180deg, #ffffff 0%, #fff6e3 100%)',
  borderTop: '1px dashed rgba(169, 94, 37, 0.32)',
}

/** Pinned section caption — nằm trên cùng cụm footer, span full width. */
const pinnedCaptionSx = {
  fontWeight: 900,
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
  textAlign: 'center' as const,
  fontSize: '0.68rem',
  color: '#000000',
  background: 'linear-gradient(180deg, #fff8e6 0%, #f4df9c 52%, #e4c36c 100%)',
  borderTop: '2px solid rgba(11, 54, 51, 0.24)',
  borderBottom: '1px solid rgba(169, 94, 37, 0.28)',
}

export const tableSx = {
  tableLayout: 'auto',
  width: '100%',
  borderCollapse: 'separate',
  borderSpacing: 0,
  '& th, & td': {
    height: 20,
    px: 0.5,
    py: 0.15,
    border: '1px solid rgba(11, 54, 51, 0.07)',
    fontSize: '0.65rem',
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: '#000000',
  },
  '& td': {
    textTransform: 'none',
  },
  '& th': {
    fontSize: '0.66rem',
    fontWeight: 800,
    letterSpacing: '0.04em',
    textAlign: 'center',
    color: '#000000',
    backgroundColor: '#fffdfb',
    backgroundImage: 'linear-gradient(180deg, #ffffff 0%, #fff9f4 55%, #fbeec3 100%)',
    borderBottom: '1px solid rgba(169, 94, 37, 0.2)',
  },
  '& td.num': {
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    fontFeatureSettings: '"tnum"',
    textOverflow: 'clip',
    minWidth: 52,
    px: 0.45,
  },
  '& td.num-center': {
    textAlign: 'center',
    fontVariantNumeric: 'tabular-nums',
    fontFeatureSettings: '"tnum"',
    textOverflow: 'clip',
    minWidth: 48,
    px: 0.4,
  },
  '& td.emphasis-cell': {
    fontWeight: 800,
  },
  '& tbody tr td': {
    bgcolor: '#ffffff',
  },
  '& tr.parent td': {
    fontWeight: 800,
    color: '#000000',
    bgcolor: '#ffffff',
  },
  '& tr.emphasis td': {
    fontWeight: 800,
    color: '#000000',
  },
  '& td.indent-cell': {
    pl: 1.6,
  },
  '& tbody tr.total td': {
    ...totalRowBodySx,
  },
  '& tfoot tr.total td': {
    ...totalRowFooterSx,
  },
  '& tfoot tr.subtotal td': {
    ...subtotalRowFooterSx,
  },
  '& tfoot tr.pinned-caption td': {
    ...pinnedCaptionSx,
    py: 0.4,
  },
}

export const compactCellOverrideSx = {
  '& th, & td': {
    height: 18,
    px: 0.25,
    py: 0.1,
    fontSize: '0.6rem',
  },
  '& th': {
    fontSize: '0.6rem',
    letterSpacing: 0,
  },
  '& td.num': {
    minWidth: 44,
    px: 0.3,
  },
  '& td.num-center': {
    minWidth: 40,
    px: 0.3,
  },
}

/** Scroll wrapper với sticky thead + total footer. */
export const scrollBoxSx = (maxHeight: number | string, allowHorizontal = false) => ({
  maxHeight,
  overflowY: 'auto' as const,
  overflowX: allowHorizontal ? ('auto' as const) : ('hidden' as const),
  border: '1px solid rgba(11, 54, 51, 0.08)',
  borderTop: 'none',
  borderRadius: '0 0 6px 6px',
  '& thead th': {
    position: 'sticky' as const,
    top: 0,
    zIndex: 3,
    backgroundColor: '#fffdfb',
    backgroundImage: 'linear-gradient(180deg, #ffffff 0%, #fff9f4 55%, #fbeec3 100%)',
    boxShadow: '0 1px 0 rgba(169, 94, 37, 0.18), inset 0 -1px 0 rgba(244, 223, 156, 0.35)',
  },
  '& tfoot tr.total td': {
    position: 'sticky' as const,
    bottom: 0,
    zIndex: 2,
    ...totalRowFooterSx,
    boxShadow: '0 -1px 0 rgba(169, 94, 37, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.72)',
  },
  /** Sub-total row đặt phía trên total row — sticky bottom với offset ~20px (chiều cao 1 row). */
  '& tfoot tr.subtotal td': {
    position: 'sticky' as const,
    bottom: 20,
    zIndex: 2,
    ...subtotalRowFooterSx,
  },
  /** Caption "TỔNG HỢP NHÀ MÁY" — sticky bottom với offset cao nhất (2 row dưới). */
  '& tfoot tr.pinned-caption td': {
    position: 'sticky' as const,
    bottom: 40,
    zIndex: 2,
    ...pinnedCaptionSx,
    py: 0.4,
  },
})

const rowKind = (row: ReportRow): string =>
  String(row.fullSymbol ?? row.type ?? row.name ?? '').trim().toLowerCase()
const isTotalRow = (row: ReportRow) => rowKind(row) === 'total'
const isSubtotalRow = (row: ReportRow) => {
  const k = rowKind(row)
  return k === 'subtotal' || k === 'wip'
}
const isFooterRow = (row: ReportRow) => isTotalRow(row) || isSubtotalRow(row)
const isParentRow = (row: ReportRow) =>
  row.isParent === 1 || row.isParent === true || row.isParent === '1'

const normalizeRowLabel = (value: unknown) => String(value ?? '').trim().toLowerCase()

const matchesRowLabel = (row: ReportRow, labels: readonly string[]) => {
  const label = normalizeRowLabel(row.type ?? row.name ?? row.fullSymbol)
  return labels.some((item) => item.toLowerCase() === label)
}

export const displayValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return ''
  if (typeof value === 'number') {
    const abs = Math.abs(value)
    const digits = abs >= 1000 ? 0 : abs >= 1 ? 2 : abs > 0 ? 4 : 0
    const formatted = new Intl.NumberFormat('en-US', { maximumFractionDigits: digits }).format(abs)
    if (value === 0) return formatted
    return value < 0 ? `(${formatted})` : formatted
  }
  return String(value)
}

export const displayAccessInteger = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return ''
  if (typeof value !== 'number') return String(value)
  if (value === 0) return '-'
  const formatted = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.abs(value))
  return value < 0 ? `(${formatted})` : formatted
}

export function FuturesReportTable({
  columns,
  rows,
  minRows = 0,
  scrollMaxHeight,
  horizontalScroll = false,
  tableMinWidth,
  density = 'normal',
  pinTotalRow = false,
  pinnedSectionTitle,
  boldText = false,
  highlightParentRows = true,
  emphasisRowLabels = [],
  emphasisColumnKeys = [],
  indentNonEmphasisFirstColumn = false,
  numericFormatter,
  numericAlign = 'right',
  freezeTitleColumns = false,
}: {
  columns: ColumnDef[]
  rows: ReportRow[]
  minRows?: number
  scrollMaxHeight?: number | string
  horizontalScroll?: boolean
  tableMinWidth?: number | string
  density?: 'normal' | 'compact'
  pinTotalRow?: boolean
  /** Caption gold band đặt trên cùng cụm footer (vd "TỔNG HỢP NHÀ MÁY · Factory totals"). */
  pinnedSectionTitle?: string
  boldText?: boolean
  highlightParentRows?: boolean
  emphasisRowLabels?: string[]
  emphasisColumnKeys?: string[]
  indentNonEmphasisFirstColumn?: boolean
  numericFormatter?: (value: unknown) => string
  numericAlign?: 'right' | 'center'
  /** Khi true, các cột text (non-numeric) đầu bảng sẽ sticky-left khi cuộn
   *  ngang. Mỗi cột sticky cần khai báo `minWidth` để tính offset chính xác. */
  freezeTitleColumns?: boolean
}) {
  const bodyRows = pinTotalRow ? rows.filter((r) => !isFooterRow(r)) : rows
  const footerRows = pinTotalRow ? rows.filter(isFooterRow) : []

  const { stickyCount, stickyLefts, stickyWidths } = useMemo(() => {
    if (!freezeTitleColumns) {
      return { stickyCount: 0, stickyLefts: [] as number[], stickyWidths: [] as number[] }
    }
    const lefts: number[] = []
    const widths: number[] = []
    let cum = 0
    for (const col of columns) {
      if (col.numeric) break
      const w = col.minWidth ?? 100
      lefts.push(cum)
      widths.push(w)
      cum += w
    }
    return { stickyCount: lefts.length, stickyLefts: lefts, stickyWidths: widths }
  }, [columns, freezeTitleColumns])

  // Body/header/footer cells dùng bg từ `tableSx` (`& tbody tr td`, `& thead th`,
  //  `& tfoot tr.total td` …) — đều opaque, đủ để che nội dung lướt qua khi sticky.
  const stickyCellSx = (idx: number) => {
    if (idx >= stickyCount) return null
    const w = stickyWidths[idx]
    return {
      position: 'sticky' as const,
      left: stickyLefts[idx],
      width: w,
      minWidth: w,
      maxWidth: w,
    }
  }

  const paddedRows = useMemo(() => {
    if (bodyRows.length >= minRows) return bodyRows
    return [...bodyRows, ...Array.from({ length: minRows - bodyRows.length }, () => ({} as ReportRow))]
  }, [minRows, bodyRows])

  const tableInnerSx = {
    ...tableSx,
    ...(density === 'compact' ? compactCellOverrideSx : {}),
    ...(tableMinWidth ? { minWidth: tableMinWidth } : {}),
    ...(boldText ? { '& th, & td': { ...tableSx['& th, & td'], fontWeight: 700 } } : {}),
  }
  const emphasisColumnSet = new Set(emphasisColumnKeys)

  const renderCell = (row: ReportRow, col: ColumnDef) => {
    const keys = col.sourceKeys ?? [col.key]
    for (const key of keys) {
      const value = row[key]
      if (value !== null && value !== undefined && value !== '') {
        return col.numeric && numericFormatter ? numericFormatter(value) : displayValue(value)
      }
    }
    return ''
  }

  /* Group header row — gom cột liên tiếp cùng `group` thành 1 cell with colSpan. */
  const groupHeaderRow = useMemo(() => {
    if (!columns.some((c) => c.group)) return null
    type Span = { group: string | null; span: number; start: number }
    const spans: Span[] = []
    let cur: Span | null = null
    for (let i = 0; i < columns.length; i++) {
      const g = columns[i].group ?? null
      if (cur && cur.group === g) {
        cur.span += 1
      } else {
        cur = { group: g, span: 1, start: i }
        spans.push(cur)
      }
    }
    return spans
  }, [columns])

  /** Đo chiều cao group-header row để dịch sticky-top của column-header row xuống
   *  bên dưới — nếu không, cả hai cùng top:0 và col-header sẽ che group-header. */
  const groupHeaderRef = useRef<HTMLTableRowElement | null>(null)
  const [groupHeaderH, setGroupHeaderH] = useState(0)

  useIsoLayoutEffect(() => {
    if (!groupHeaderRow) {
      setGroupHeaderH(0)
      return
    }
    const el = groupHeaderRef.current
    if (!el) return
    const measure = () => {
      const h = el.offsetHeight
      setGroupHeaderH((prev) => (prev === h ? prev : h))
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [groupHeaderRow])

  const table = (
    <Table size="small" sx={tableInnerSx}>
      <TableHead>
        {groupHeaderRow ? (
          <TableRow ref={groupHeaderRef} className="group-header">
            {groupHeaderRow.map((s) => {
              const spanEnd = s.start + s.span
              const isStickySpan = stickyCount > 0 && s.start === 0 && spanEnd <= stickyCount
              const stickyBlockWidth = isStickySpan
                ? stickyWidths.slice(0, spanEnd).reduce((a, b) => a + b, 0)
                : undefined
              return (
              <TableCell
                key={`g-${s.start}`}
                colSpan={s.span}
                className={isStickySpan ? 'sticky-col' : undefined}
                sx={{
                  textAlign: 'center',
                  fontWeight: 800,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  whiteSpace: 'pre-line',       // hỗ trợ "\n" → 2 dòng VN + EN
                  lineHeight: 1.15,
                  py: 0.4,
                  color: s.group ? '#000000' : 'transparent',
                  background: s.group
                    ? 'linear-gradient(180deg, #fff8e6 0%, #f4df9c 52%, #e4c36c 100%)'
                    : '#fffdfb',
                  borderBottom: s.group ? '2px solid rgba(11, 54, 51, 0.24)' : '1px solid rgba(169, 94, 37, 0.2)',
                  '& .group-en': {
                    display: 'block',
                    fontSize: '0.55rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    color: '#000000',
                    letterSpacing: 0,
                    fontStyle: 'italic',
                    lineHeight: 1.15,
                  },
                  '& .group-vi': {
                    display: 'block',
                    lineHeight: 1.15,
                  },
                  ...(isStickySpan
                    ? {
                        position: 'sticky' as const,
                        left: 0,
                        width: stickyBlockWidth,
                        minWidth: stickyBlockWidth,
                        maxWidth: stickyBlockWidth,
                      }
                    : {}),
                }}
              >
                {s.group ? (() => {
                  const parts = s.group.split('\n')
                  const vi = parts[0]
                  const en = parts.slice(1).join('\n')
                  return (
                    <>
                      <span className="group-vi">{vi}</span>
                      {en ? <span className="group-en">{en}</span> : null}
                    </>
                  )
                })() : ''}
              </TableCell>
              )
            })}
          </TableRow>
        ) : null}
        <TableRow className="col-header">
          {columns.map((col, colIndex) => {
            const sticky = stickyCellSx(colIndex)
            return (
            <TableCell
              key={col.key}
              className={sticky ? 'sticky-col' : undefined}
              sx={{
                ...(col.width ? { width: col.width } : {}),
                ...(col.minWidth ? { minWidth: col.minWidth } : {}),
                ...(sticky ?? {}),
              }}
            >
              {col.label}
            </TableCell>
            )
          })}
        </TableRow>
      </TableHead>
      <TableBody>
        {paddedRows.map((row, index) => {
          const totalInline = !pinTotalRow && isTotalRow(row)
          const parent = highlightParentRows && isParentRow(row)
          const emphasis = matchesRowLabel(row, emphasisRowLabels)
          const className = [totalInline ? 'total' : '', parent ? 'parent' : '', emphasis ? 'emphasis' : '']
            .filter(Boolean)
            .join(' ') || undefined
          return (
            <TableRow
              key={`${String(row.fullSymbol ?? row.type ?? row.name ?? 'blank')}-${index}`}
              className={className}
            >
              {columns.map((col, colIndex) => {
                const sticky = stickyCellSx(colIndex)
                return (
                <TableCell
                  key={col.key}
                  className={[
                    col.numeric ? (numericAlign === 'center' ? 'num-center' : 'num') : '',
                    emphasisColumnSet.has(col.key) ? 'emphasis-cell' : '',
                    indentNonEmphasisFirstColumn && colIndex === 0 && !totalInline && !parent && !emphasis
                      ? 'indent-cell'
                      : '',
                    sticky ? 'sticky-col' : '',
                  ]
                    .filter(Boolean)
                    .join(' ') || undefined}
                  sx={sticky ?? undefined}
                >
                  {renderCell(row, col)}
                </TableCell>
                )
              })}
            </TableRow>
          )
        })}
      </TableBody>
      {footerRows.length > 0 ? (
        <TableFooter>
          {pinnedSectionTitle ? (
            <TableRow key="pinned-caption" className="pinned-caption">
              <TableCell colSpan={columns.length}>{pinnedSectionTitle}</TableCell>
            </TableRow>
          ) : null}
          {footerRows.map((row, i) => {
            const cls = isSubtotalRow(row) ? 'subtotal' : 'total'
            return (
              <TableRow key={`${cls}-${i}`} className={cls}>
                {columns.map((col, colIndex) => {
                  const sticky = stickyCellSx(colIndex)
                  const cellClass = [
                    col.numeric ? (numericAlign === 'center' ? 'num-center' : 'num') : '',
                    sticky ? 'sticky-col' : '',
                  ]
                    .filter(Boolean)
                    .join(' ') || undefined
                  return (
                  <TableCell
                    key={col.key}
                    className={cellClass}
                    sx={sticky ?? undefined}
                  >
                    {renderCell(row, col)}
                  </TableCell>
                  )
                })}
              </TableRow>
            )
          })}
        </TableFooter>
      ) : null}
    </Table>
  )

  if (scrollMaxHeight) {
    return (
      <Box
        sx={{
          ...scrollBoxSx(scrollMaxHeight, horizontalScroll),
          // Group-header row stays at top:0 (from base `& thead th` rule).
          // Column-header row shifts down by group-header height so cả 2 dòng đều sticky.
          '& thead tr.col-header th': {
            top: `${groupHeaderH}px`,
          },
          // Sticky-left cells cần z-index cao hơn các ô cùng row để không bị che
          // khi cuộn ngang. Stack: thead group > thead col > tfoot total-col > tbody col.
          '& thead tr.group-header th.sticky-col': { zIndex: 5 },
          '& thead tr.col-header th.sticky-col': { zIndex: 4 },
          '& tfoot tr td.sticky-col': { zIndex: 3 },
          '& tbody tr td.sticky-col': { zIndex: 2 },
        }}
      >
        {table}
      </Box>
    )
  }
  return table
}

/** Section wrapper — title band + ReportTable. */
export function FuturesReportSection({
  title,
  rows,
  columns,
  minRows,
  titleSx = sectionTitleSx,
  scrollMaxHeight,
  pinTotalRow,
  boldText,
  highlightParentRows,
  emphasisRowLabels,
  emphasisColumnKeys,
  indentNonEmphasisFirstColumn,
  numericFormatter,
  numericAlign,
}: {
  title: string
  rows: ReportRow[]
  columns: ColumnDef[]
  minRows?: number
  titleSx?: object
  scrollMaxHeight?: number | string
  pinTotalRow?: boolean
  boldText?: boolean
  highlightParentRows?: boolean
  emphasisRowLabels?: string[]
  emphasisColumnKeys?: string[]
  indentNonEmphasisFirstColumn?: boolean
  numericFormatter?: (value: unknown) => string
  numericAlign?: 'right' | 'center'
}) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={titleSx}>{title}</Box>
      <FuturesReportTable
        columns={columns}
        rows={rows}
        minRows={minRows}
        scrollMaxHeight={scrollMaxHeight}
        pinTotalRow={pinTotalRow}
        boldText={boldText}
        highlightParentRows={highlightParentRows}
        emphasisRowLabels={emphasisRowLabels}
        emphasisColumnKeys={emphasisColumnKeys}
        indentNonEmphasisFirstColumn={indentNonEmphasisFirstColumn}
        numericFormatter={numericFormatter}
        numericAlign={numericAlign}
      />
    </Box>
  )
}
